import { ICommonContextStates } from "@/contexts/CommonContext";
import useCommonContext from "@/hooks/useCommonContext";
import { isTacticStaff } from "@/lib/featureFlags";
import { convertTZ, parseApiDate } from "@/lib/utils";
import { IResource, IUserFull, Office, Tag } from "@gettactic/api";
import { StartEndTimeMode, createMapLink } from "@gettactic/helpers";
import { UseDisclosureReturn, useDisclosure } from "@gettactic/helpers";
import {
  UseEventHandlerManagerReturn,
  useEventHandlerManager
} from "@gettactic/helpers/src/hooks/useEventHandlerManager";
import { InteractiveZonesInfo } from "@gettactic/helpers/src/map/types";
import { setStorageFloor } from "@gettactic/helpers/src/resources/setStorageFloor";
import {
  IFloorWithResources,
  IResourceWithSchedules,
  IUserWithResource,
  useAuthenticated,
  useGetAmenities,
  useOfficeResources
} from "@gettactic/hooks";
import { useDefaultOfficeHours, setEndTimeWithDayOverflow } from "@gettactic/hooks";
import { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import { MapInteractiveZoneActionOptions } from "@gettactic/map-builder/src/types";
import { useGlobalSearch } from "@webV2/components/global-search";
import { MapModalParams } from "@webV2/components/map-modal";
import {
  UseMapViewOverlaysPopperReturn,
  useMapViewOverlaysPopper
} from "@webV2/map/MapView/useMapViewOverlaysPopper";
import { useReserve } from "@webV2/providers/ReserveProvider/context";
import {
  addMinutes,
  isSameDay,
  format,
  parseISO
} from "date-fns";
import { useRouter } from "next/router";
import React, { useMemo, useRef } from "react";
import { MapNavigationParams } from "../components/global-search/GlobalSearchProvider";
import { zonedTimeToUtc } from "date-fns-tz";
import { TIME_SELECT_INTERVAL_MINUTES } from "@v2/components/time/constants";

interface MapProviderEvents {
  onHighlightResource: (resource: IResource | null, userId?: string) => void;
  onFocusResource: (resource: IResource | null, options?: { noZoom?: boolean }) => void;
  onLocateResource: (resource: IResource | null) => void;
  onHighlightUserResource: (resources: IResource[] | null) => void;
  onChangeFloor: (
    newFloor: IResource | null,
    oldFloor: IResource | null
  ) => void;
}

export interface MapPageOptions {
  schedulable?: IResourceWithSchedules[];
  floors?: IResource[];
  floorsWithResources?: IFloorWithResources[];
  usersAtOffice?: IUserWithResource[];
  resources?: IResource[];
  allResources?: IResource[];
  floor: IResource | null;
  currentOffice: Office;
  startDate: Date | null;
  endDate: Date | null;
  timeMode?: StartEndTimeMode;
  setTimeMode: (timeMode: StartEndTimeMode) => void;
  isDataLoading?: boolean;
  selectedResource: IResource | null;
  isMapEditing: boolean;
  isEmbedded: boolean;
  isMapBuilderMap: boolean;
  highlightedResource: IResource | null;
  poppers: UseMapViewOverlaysPopperReturn;
  openResourceModal: boolean;
  editGroupId: string | null;
  mapBuilderData: MapCanvasValue | undefined;
  editingMapDisclosure: UseDisclosureReturn;
  resourceIdHovering: string | null;
  focusedResource: string | null;
  events: UseEventHandlerManagerReturn<MapProviderEvents>;
  amenities: Tag[];
  amenitiesFilter: Tag[];
  userFocusDisclosure: UseDisclosureReturn<IUserFull>;
  mapFiltersDisclosure: UseDisclosureReturn;
  zonesInfo: InteractiveZonesInfo;
  locateResourceId: string | null;
  liveStartDate: Date | null;
  liveEndDate: Date | null;
  mapChangesCount: number;
  forceMapBuilder: boolean;
  filterQuery: string;
  setFilterQuery: React.Dispatch<React.SetStateAction<string>>;
  isReservationMode: boolean;
  resourceInReservation: string | null;
  fullscreenMapDisclosure: UseDisclosureReturn;
  defaultTab?: MapModalParams["defaultTab"];
  setSection: (section: MapModalParams["defaultTab"]) => void;
  selectedUsersInCalendarIntegrations: {
    value: string;
    label: string;
    picture?: string;
    profile_photo_url?: string;
  }[];
  mapInstanceId: string;
  isReservationCardIsOpen: boolean;
}

export interface MapStateActions {
  setFloor: (newFloor: IResource | null) => void;
  changeOffice: (officeId: string) => void;
  changeEndTime: (date: Date | null) => void;
  changeStartDate: (date: Date | null) => void;
  setSelectedResource: React.Dispatch<React.SetStateAction<IResource | null>>;
  setHighlightedResource: React.Dispatch<
    React.SetStateAction<IResource | null>
  >;
  setOpenResourceModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditGroupId: React.Dispatch<React.SetStateAction<string | null>>;
  highlightResource: (
    resId: string | null,
    target: Element | MapInteractiveZoneActionOptions | null
  ) => void;
  setForceMapBuilder: React.Dispatch<React.SetStateAction<boolean>>;
  setResourceIdHovering: React.Dispatch<React.SetStateAction<string | null>>;
  setFocusedResource: React.Dispatch<React.SetStateAction<string | null>>;
  startReserve: (resource: IResource) => void;
  setResourcesIdFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  applyAmenitiesFilter: (ids: string[]) => void;
  selectResource: (
    resId: string,
    center?: boolean,
    callback?: () => void,
    officeId?: string,
    floorId?: string,
    date?: Date,
    preserveData?: boolean
  ) => void;
  setLocateResourceId: React.Dispatch<React.SetStateAction<string | null>>;
  setZonesInfo: React.Dispatch<React.SetStateAction<InteractiveZonesInfo>>;
  setMapChangesCount: React.Dispatch<React.SetStateAction<number>>;
  setSelectedUsersInCalendarIntegrations: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
        label: string;
        picture?: string;
        profile_photo_url?: string;
      }[]
    >
  >;
  onFloorReady: (floorId: string) => void;
}

export type MapPageState = MapPageOptions &
  MapStateActions & {
    isOnReservationModal?: boolean;
    isReservationCardIsOpen?: boolean;
  };

export const MapPageContext = React.createContext<MapPageState | null>(null);

export interface EditingMapDisclosureData {
  mode: "svg" | "map-builder";
}

export interface MapProviderProps {
  isEmbedded?: boolean;
  officeId?: string;
  floorId?: string;
  startDate?: string | null;
  // startTime?: string | null;
  initialFloorId?: string | null;
  initialSearchText?: string;
  resourceId?: string;
  reservationModalIsOpen?: boolean;
  initialEndDate?: string | null;
  isOnReservationModal?: boolean;
  isReservationCardIsOpen?: boolean;
  section?: MapModalParams["defaultTab"];
  initialTimeMode?: StartEndTimeMode;
  disableUrlOfficeSync?: boolean;
}

export const MapProvider: React.FC<
  React.PropsWithChildren<MapProviderProps>
> = ({
  children,
  isEmbedded = false,
  officeId: controlledOfficeId,
  floorId: controlledFloorId,
  startDate: controlStartDate,
  // startTime: controlStartTime,
  initialFloorId,
  resourceId,
  reservationModalIsOpen,
  isReservationCardIsOpen,
  initialEndDate,
  isOnReservationModal,
  section: controlledSection,
  initialSearchText,
  initialTimeMode,
  disableUrlOfficeSync
}) => {
  // Let's manage some events "stateless" to prevent re-renders when not strictly necessary ( useful on huge maps )
  const events = useEventHandlerManager<MapProviderEvents>();
  const router = useRouter();
  const mapInstanceId = React.useId();
  const { reserveData, openReserveModal, isReserveOpen } = useReserve();

  const {
    userContext: { authenticatedUser, currentOffice: contextSelectedOffice },
    setUserContext
  } = useAuthenticated();
  const { setCommon, start: commonStart } = useCommonContext();
  const [locateResourceId, setLocateResourceId] = React.useState<string | null>(
    null
  );

  // Pending locate request to be executed when the correct floor SVG is ready
  const pendingLocateRef = React.useRef<{
    resourceId: string;
    center: boolean;
  } | null>(null);

  // Pending reservation request to be executed when resources are loaded
  const pendingReservationRef = React.useRef<{
    resourceId: string;
  } | null>(null);

  // Pending resource selection from URL (non-reservation mode) to be executed when resources are loaded
  const pendingResourceSelectionRef = React.useRef<{
    resourceId: string;
    floorId?: string;
    center: boolean;
  } | null>(null);

  // Track if we've already auto-opened the reserve modal in reservation mode
  const hasAutoOpenedRef = React.useRef(false);

  const [mapChangesCount, setMapChangesCount] = React.useState(0);

  // Get command palette context to register map navigator and controllers
  const {
    registerMapNavigator,
    registerOpenResourceInfo,
    registerMapControllers
  } = useGlobalSearch();

  const defaultOfficeFromPropIsApplied = useRef(false);

  const currentOffice = useMemo(() => {
    // Apply `officeId` prop only as an initial default (so the user can still switch offices later),
    // but don't "lock it in" before the office data is actually available.
    if (controlledOfficeId && !defaultOfficeFromPropIsApplied.current) {
      const officeFromProp =
        authenticatedUser?.offices?.byId?.[controlledOfficeId];
      if (officeFromProp) {
        defaultOfficeFromPropIsApplied.current = true;
        return officeFromProp;
      }
      return contextSelectedOffice;
    }
    return contextSelectedOffice;
  }, [controlledOfficeId, authenticatedUser?.offices?.byId, contextSelectedOffice]);

  const amenitiesResponse = useGetAmenities();
  const amenities = amenitiesResponse?.data?.result?.tags || [];

  const today = convertTZ(new Date(), currentOffice.time_zone);

  const urlParamsReady = React.useRef<boolean>(false);
  const isInitialized = React.useRef<boolean>(false);
  const lastSyncedUrlParams = React.useRef<string>("");
  const defaultsAppliedRef = React.useRef<boolean>(false);

  // Helper to get the correct timezone for initialization
  // If officeId prop is provided, use that office's timezone; otherwise use context office
  const getInitialTimeZone = () => {
    if (controlledOfficeId) {
      const officeFromProp = authenticatedUser?.offices?.byId?.[controlledOfficeId];
      if (officeFromProp) return officeFromProp.time_zone;
    }
    return contextSelectedOffice.time_zone;
  };

  const [_startDate, setStartDate] = React.useState<Date | null>(() => {
    const tz = getInitialTimeZone();
    if (controlStartDate) {
      if (controlStartDate.includes("T")) {
        return zonedTimeToUtc(controlStartDate, tz);
      }
      return parseApiDate(controlStartDate);
    }
    if (commonStart) {
      return commonStart;
    }
    return new Date(convertTZ(new Date(), tz).getTime());
  });

  const [_endDate, setEndDate] = React.useState<Date | null>(() => {
    const tz = getInitialTimeZone();
    if (initialEndDate) {
      return zonedTimeToUtc(initialEndDate, tz);
    }
    return null;
  });
  const [liveStartDate, setLiveStartDate] = React.useState<Date | null>(
    new Date()
  );
  const [liveEndDate, setLiveEndDate] = React.useState<Date | null>(null);

  const [resourcesIdFilter, setResourcesIdFilter] = React.useState<string[]>(
    []
  );
  const [filterQuery, setFilterQuery] = React.useState(initialSearchText || "");
  const [section, setSection] = React.useState<MapModalParams["defaultTab"]>(
    controlledSection || "people"
  );
  const [editGroupId, setEditGroupId] = React.useState<string | null>(null);
  const [openResourceModal, setOpenResourceModal] = React.useState(false);
  const [openSidePanel, setOpenSidePanel] = React.useState(false);
  const isTacticStaffAuthenticatedUser = !!isTacticStaff(authenticatedUser);

  const [timeMode, setTimeMode] = React.useState<StartEndTimeMode>("manual");

  const [resourceIdHovering, setResourceIdHovering] = React.useState<
    string | null
  >(null);

  const [selectedResource, setSelectedResource] =
    React.useState<IResource | null>(null);
  const [highlightedResource, setHighlightedResource] =
    React.useState<IResource | null>(null);
  const [focusedResource, setFocusedResource] = React.useState<string | null>(
    null
  );
  const [
    selectedUsersInCalendarIntegrations,
    setSelectedUsersInCalendarIntegrations
  ] = React.useState<
    {
      value: string;
      label: string;
      picture?: string;
      profile_photo_url?: string;
    }[]
  >([]);

  const startDate = timeMode === "manual" ? _startDate : liveStartDate;
  const endDate = timeMode === "manual" ? _endDate : liveEndDate;

  const mapFiltersDisclosure = useDisclosure();

  const editingMapDisclosure = useDisclosure<EditingMapDisclosureData>();
  const userFocusDisclosure = useDisclosure<IUserFull>();
  const fullscreenMapDisclosure = useDisclosure();
  const isMapEditing = editingMapDisclosure.isOpen;

  const [amenitiesFilter, setAmenitiesFilter] = React.useState<Tag[]>([]);

  const applyAmenitiesFilter = (ids: string[]) => {
    if (!amenities || amenities.length === 0) {
      setAmenitiesFilter([]);
      return;
    }
    const filtered = amenities.filter((amenity) => ids.includes(amenity.id));
    setAmenitiesFilter(filtered);
  };

  const {
    schedulable,
    floors,
    floorsWithResources,
    usersAtOffice,
    resources,
    loading: isDataLoading,
    floor,
    setFloor: _setFloor,
    reload: reloadResources
  } = useOfficeResources({
    officeId: disableUrlOfficeSync
      ? currentOffice.id
      : (urlParamsReady.current ? currentOffice.id : null),
    startDate,
    endDate,
    filterQuery,
    entireOffice: isMapEditing || openSidePanel,
    getAllMaps: isTacticStaffAuthenticatedUser,
    coreApi: true,
    tags: amenitiesFilter.map((tag) => tag.id),
    floorId: controlledFloorId,
    initialFloorId,
    allowNoFloor: true
  });

  const changeOffice = React.useCallback(
    (officeId: string) => {
      if (officeId === currentOffice.id) return;

      // In modal/embedded contexts we must not navigate to `/map` when switching offices.
      // We rely on the modal URL sync (or parent page state) and only update the user context.
      if (disableUrlOfficeSync || reservationModalIsOpen || isOnReservationModal || isEmbedded) {
        setUserContext({
          type: "updateCurrentOffice",
          payload: officeId
        });
        return;
      }

      const mapLink = createMapLink({
        date: startDate ? format(startDate, "yyyy-MM-dd'T'HH:mm:ss") : "",
        endTime: endDate ? format(endDate, "yyyy-MM-dd'T'HH:mm:ss") : "",
        officeId: officeId,
        timeMode: timeMode
      });

      router.replace(mapLink, undefined, { shallow: true });

      setUserContext({
        type: "updateCurrentOffice",
        payload: officeId
      });
    },
    [
      currentOffice.id,
      startDate,
      timeMode,
      endDate,
      router,
      setUserContext,
      disableUrlOfficeSync,
      reservationModalIsOpen,
      isOnReservationModal,
      isEmbedded
    ]
  );

  const setFloor = (newFloor: IResource | null) => {
    if (newFloor?.id === floor?.id) return;

    _setFloor(newFloor);
    setStorageFloor(newFloor?.id);
    setOpenResourceModal(false);
    setSelectedResource(null);
    setHighlightedResource(null);
    setFocusedResource(null);

    events.trigger("onChangeFloor", newFloor, floor);
  };

  React.useEffect(() => {
    if (timeMode !== "live") return;
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    let recurringTimeoutId: NodeJS.Timeout;

    function setLiveTick() {
      const now = new Date();
      setLiveStartDate(now); // 30 minutes ago
      setLiveEndDate(addMinutes(now, 1));
      reloadResources();
    }

    function startUpdates() {
      setLiveTick();

      // Schedule next update in 60s
      recurringTimeoutId = setTimeout(startUpdates, 60000);
    }

    const now = new Date();
    const msToNextMinutePlus5s = (60 - now.getSeconds()) * 1000;

    setLiveTick();
    timeoutId = setTimeout(() => {
      startUpdates();
    }, msToNextMinutePlus5s);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(recurringTimeoutId);
    };
  }, [timeMode]);

  const defaultOfficeHoursCalculator = useDefaultOfficeHours(currentOffice);

  /**
   * Normalizes a date by zeroing out seconds and milliseconds.
   * This prevents drift from fractional seconds while preserving custom times
   * (e.g., 10:07 remains 10:07, not rounded to 10:00 or 10:15).
   * The 1-minute offset bug was caused by seconds/milliseconds in time calculations,
   * not by minutes being off-interval.
   */
  const normalizeTimePrecision = React.useCallback((date: Date): Date => {
    const normalized = new Date(date);
    normalized.setSeconds(0, 0);
    return normalized;
  }, []);

  /**
   * Checks if a date's minutes are on the configured time interval.
   * Used to determine if we should round to time intervals when preserving duration.
   */
  const isOnTimeInterval = React.useCallback((date: Date): boolean => {
    const minutes = date.getMinutes();
    return minutes % TIME_SELECT_INTERVAL_MINUTES === 0;
  }, []);

  const getOrCalculateDefaultEndDate = React.useCallback((start: Date, providedEnd?: Date) => {
    if (providedEnd && providedEnd.getTime() > start.getTime()) return providedEnd;

    const defaults = defaultOfficeHoursCalculator(start);
    if (defaults) {
      const end = new Date(start.getTime());
      setEndTimeWithDayOverflow(end, start, defaults.endHour, defaults.endMinutes);
      if (end.getTime() > start.getTime()) return end;
    }
    
    // Last resort: +1 hour
    return new Date(start.getTime() + 60 * 60 * 1000);
  }, [defaultOfficeHoursCalculator]);

  const changeEndTime = React.useCallback(
    (date: Date | null) => {
      if (
        date === null ||
        startDate === null ||
        date.getTime() - startDate.getTime() <= 0
      ) {
        // avoid querying api with invalid values
        return;
      }
      // Only zero out seconds/milliseconds to prevent drift, preserve custom minutes
      const normalizedEndDate = normalizeTimePrecision(date);
      setEndDate(normalizedEndDate);
    },
    [startDate, normalizeTimePrecision]
  );

  const changeStartDate = React.useCallback(
    (date: Date | null) => {
      if (date === null) {
        return;
      }
      const defaultHours = defaultOfficeHoursCalculator(date);
      let updateDefaultHours = false;

      // Only zero out seconds/milliseconds to prevent drift, preserve custom minutes
      const normalizedDate = normalizeTimePrecision(date);

      // Check if the incoming date has a specific time set (not midnight)
      const hasSpecificTime =
        normalizedDate.getHours() !== 0 ||
        normalizedDate.getMinutes() !== 0 ||
        normalizedDate.getSeconds() !== 0;

      // Apply default office hours if:
      // 1. The day changed AND no specific time is set, OR
      // 2. We're in reservation mode and the time is midnight (from Agenda)
      const shouldApplyDefaultHours =
        (startDate && !isSameDay(normalizedDate, startDate) && !hasSpecificTime) ||
        (reservationModalIsOpen && !hasSpecificTime);

      if (shouldApplyDefaultHours && defaultHours) {
        normalizedDate.setHours(defaultHours.startHour, defaultHours.startMinutes, 0);
        updateDefaultHours = true;
      }

      // When one of the bounds is missing (e.g. "live" mode with no end date yet),
      // fall back to office defaults so startDate can still move.
      // Normalize seconds/milliseconds before calculating difference to prevent drift,
      // but preserve minutes to support custom times (e.g., 10:07)
      const effectiveStart = startDate ? normalizeTimePrecision(startDate) : normalizedDate;
      const effectiveEnd = endDate
        ? normalizeTimePrecision(endDate)
        : getOrCalculateDefaultEndDate(effectiveStart);
      
      // Check if original times were on time intervals - if so, we'll round the result
      const startWasOnInterval = startDate ? isOnTimeInterval(startDate) : isOnTimeInterval(normalizedDate);
      const endWasOnInterval = endDate ? isOnTimeInterval(endDate) : false;

      let currentDiff = effectiveEnd.getTime() - effectiveStart.getTime();

      // If durations are collapsed or invalid, default to office defaults
      if (currentDiff <= 0) {
        const fallbackEnd = getOrCalculateDefaultEndDate(effectiveStart);
        currentDiff = fallbackEnd.getTime() - effectiveStart.getTime();
      }

      setStartDate(normalizedDate);
      setCommon((context: ICommonContextStates | null) => ({
        ...context,
        start: normalizedDate
      }));

      if (updateDefaultHours && defaultHours && endDate) {
        setEndTimeWithDayOverflow(endDate, normalizedDate, defaultHours.endHour, defaultHours.endMinutes);
      }

      // If endDate exists and is on the same day, we might not need to update it
      // But if endDate is null, we always need to set it
      if (endDate && isSameDay(endDate, normalizedDate)) {
        return;
      }
      const maxTime = new Date(normalizedDate);
      maxTime.setHours(23, 30, 0);
      
      // Calculate new end date and normalize seconds/milliseconds
      const newEndDate = normalizeTimePrecision(
        new Date(normalizedDate.getTime() + currentDiff)
      );
      
      // If both original times were on time intervals, round the result to time interval
      // This preserves the behavior for standard interval selections while allowing custom times
      if (startWasOnInterval && (endWasOnInterval || !endDate)) {
        const minutes = newEndDate.getMinutes();
        const roundedMinutes = Math.round(minutes / TIME_SELECT_INTERVAL_MINUTES) * TIME_SELECT_INTERVAL_MINUTES;
        newEndDate.setMinutes(roundedMinutes, 0, 0);
      }
      
      if (updateDefaultHours && defaultHours) {
        setEndTimeWithDayOverflow(newEndDate, normalizedDate, defaultHours.endHour, defaultHours.endMinutes);
      }
      
      // Ensure newEndDate is valid - if it's before normalizedDate, use maxTime or recalculate
      if (newEndDate.getTime() <= normalizedDate.getTime()) {
        // If the calculated end date is invalid, use maxTime for same day or recalculate with defaults
        const recalculatedEnd = getOrCalculateDefaultEndDate(normalizedDate);
        setEndDate(isSameDay(normalizedDate, recalculatedEnd) ? recalculatedEnd : maxTime);
      } else {
        setEndDate(isSameDay(normalizedDate, newEndDate) ? newEndDate : maxTime);
      }
    },
    [
      endDate,
      defaultOfficeHoursCalculator,
      startDate,
      setCommon,
      normalizeTimePrecision,
      isOnTimeInterval,
      getOrCalculateDefaultEndDate
      // reservationModalIsOpen // this one doesn't change
    ]
  );
  // Keep track of current state in a ref to access it in useEffect without triggering re-runs
  const stateRef = React.useRef({ startDate, endDate, floor });
  stateRef.current = { startDate, endDate, floor };

  /* 
    Unified Initialization Effect 
    Handles:
    1. URL Parsing (Hash & Query)
    2. Office switching if URL differs
    3. Floor switching if URL differs
    4. Date/Time initialization (URL params > Defaults)
    5. Reservation mode setup
  */
  React.useEffect(() => {
    if (!router.isReady) {
      return;
    }
    urlParamsReady.current = true;

    // Parse URL (support both hash-based old URLs and standard query params)
    const [path, hash] = ((router.asPath as string) || "").split("#");
    const parsedHash = new URLSearchParams(hash);

    // Priority: Prop > Hash Param > Query Param
    const getParam = (name: string, queryFallback?: string) => {
      const val = parsedHash.get(name);
      if (val) return val;
      
      const qKey = queryFallback || name;
      if (router.query[qKey]) return String(router.query[qKey]);
      
      return null;
    };

    const resourceIdStr = getParam("resource", "resourceId");
    const officeIdStr = getParam("office", "officeId");
    const floorIdStr = controlledFloorId || getParam("floor", "floorId");

    // Date/Time params
    // Note: 'date' in query usually implies startDate
    const dateStr =
      controlStartDate || getParam("date") || getParam("startTime");
    const startTimeStr = getParam("startTime") || getParam("date");
    const endDateStr = initialEndDate || getParam("endTime");
    const timeModeStr = getParam("timeMode") as StartEndTimeMode | null;

    // 1. Office Check
    let existingOffice: Office | null = null;
    if (!disableUrlOfficeSync) {
      if (
        !controlledOfficeId &&
        officeIdStr &&
        currentOffice.id !== officeIdStr
      ) {
        existingOffice =
          authenticatedUser.offices?.offices.find(
            (o) => o.id === officeIdStr
          ) ?? null;
        if (existingOffice) {
          setUserContext({ type: "updateCurrentOffice", payload: officeIdStr });
        }
      }
    }

    // 2. Build current URL params string to check for changes
    const currentParamsString = JSON.stringify({
      resourceIdStr,
      officeIdStr,
      floorIdStr,
      dateStr,
      startTimeStr,
      endDateStr,
      timeModeStr
    });

    const urlParamsChanged =
      currentParamsString !== lastSyncedUrlParams.current;

    // If we are already initialized and the relevant URL params haven't changed,
    // we should NOT re-run the initialization logic (which would overwrite manual state).
    // EXCEPT if we are waiting for office defaults to be applied and they haven't been yet.
    const shouldSkipInitialization = 
      isInitialized.current && 
      !urlParamsChanged && 
      (!!dateStr || defaultsAppliedRef.current);

    if (shouldSkipInitialization) {
      return;
    }

    // 0. Time Mode Sync
    const effectiveTimeModeSync = timeModeStr || timeMode;
    if (timeModeStr && timeModeStr !== timeMode) {
      if (timeModeStr === "live" || timeModeStr === "manual") {
        setTimeMode(timeModeStr);
      }
    }

    // TODO - REMOVED FOR NOW BECAUSE IT BREAKS THE FLOOR SWITCH. WE SHOULD INVESTIGATE ABOUT RESERVE MODAL
    // 3. Floor Check
    /*    if (floorIdStr && floorIdStr !== stateRef.current.floor?.id) {
      const floorExists = floors?.find((x) => floorIdStr === x.id);
      console.log("FLOOR EXISTS", floorExists);
      if (floorExists) {
        setFloor(floorExists);
        if (!controlledFloorId) {
          setStorageFloor(floorIdStr);
        }
      }
    }*/

    // 3. Date/Time Initialization
    // We only want to run initialization if we haven't set up valid dates yet OR if URL changes
    // But since this effect runs on deps, we need to be careful not to overwrite user interactions.
    // However, the goal is "Sync with URL".

    const tz = existingOffice?.time_zone || currentOffice.time_zone;

    // Determine target Start Date
    let targetStartDate: Date | null = null;
    if (dateStr) {
      // If full ISO string or just date
      if (dateStr.includes("T")) {
        targetStartDate = parseISO(dateStr);
      } else {
        targetStartDate = parseApiDate(dateStr);
      }
    }

    // Determine target End Date
    let targetEndDate: Date | null = null;
    if (endDateStr) {
      if(endDateStr.includes("T")) {
        targetEndDate = parseISO(endDateStr);
      } else {
        targetEndDate = parseApiDate(endDateStr);
      }
    }
    // Logic for applying defaults if NO URL params
    // const defaultOfficeHours = defaultOfficeHoursCalculator(
    //   targetStartDate || today
    // );

    // if (
    //   targetStartDate &&
    //   dateStr &&
    //   !dateStr.includes("T") &&
    //   defaultOfficeHours
    // ) {
    //   targetStartDate.setHours(
    //     defaultOfficeHours.startHour,
    //     defaultOfficeHours.startMinutes,
    //     0
    //   );
    // }

    // Access current state from ref to avoid dependency cycle
    const currentStart = stateRef.current.startDate
      ? new Date(stateRef.current.startDate)
      : null;
    const currentEnd = stateRef.current.endDate
      ? new Date(stateRef.current.endDate)
      : null;

    if (targetStartDate && !controlStartDate) {
      // URL/Prop provided a target date (and potentially times)
      const startChanged = 
        !currentStart ||
        Math.abs(targetStartDate.getTime() - currentStart.getTime()) > 1000;
      
      const endChanged = 
        targetEndDate && 
        (!currentEnd || Math.abs(targetEndDate.getTime() - currentEnd.getTime()) > 1000);

      // If we have a date but NO specific end time in the URL, apply office defaults
      const isDateOnlyFromUrl = dateStr && !dateStr.includes("T") && !startTimeStr?.includes("T") && !endDateStr;
      const isMissingEndTimeFromUrl = (dateStr || startTimeStr) && !endDateStr;
      
      let finalTargetStart = targetStartDate;
      let finalTargetEnd = targetEndDate;

      if ((isDateOnlyFromUrl || isMissingEndTimeFromUrl) && effectiveTimeModeSync !== "live") {
        const defaultOfficeHours = defaultOfficeHoursCalculator(targetStartDate || today);
        if (defaultOfficeHours) {
          if (isDateOnlyFromUrl) {
            finalTargetStart = new Date((targetStartDate || today).getTime());
            finalTargetStart.setHours(defaultOfficeHours.startHour, defaultOfficeHours.startMinutes, 0);
          }
          
          if (isMissingEndTimeFromUrl) {
            finalTargetEnd = new Date((targetStartDate || finalTargetStart || today).getTime());
            const startForEnd = finalTargetStart || targetStartDate || today;
            setEndTimeWithDayOverflow(finalTargetEnd, startForEnd, defaultOfficeHours.endHour, defaultOfficeHours.endMinutes);
          }
        }
      }

      // Validate end date against start date
      const effectiveEndDate = getOrCalculateDefaultEndDate(finalTargetStart || today, finalTargetEnd || undefined);

      if (startChanged || endChanged || isDateOnlyFromUrl || (isMissingEndTimeFromUrl && effectiveTimeModeSync !== "live")) {
        setStartDate(finalTargetStart);
        setEndDate(effectiveEndDate);

        setCommon((context: ICommonContextStates | null) => ({
          ...context,
          start: finalTargetStart
        }));
      }
    } else {
      // No URL/Prop date -> Apply Defaults (if not already set correctly)
      // We assume if startDate is "today midnight" (initial state) we might want to apply defaults
      // Or if we strictly want to enforce "On Load, if no URL, set to Office Hours".
      const defaultOfficeHours = defaultOfficeHoursCalculator(
        targetStartDate || today
      );
      if (defaultOfficeHours) {
        defaultsAppliedRef.current = true;
        // Calculate today's default hours
        const initializedDate = new Date(
          (_startDate || new Date(convertTZ(new Date(), tz))).getTime()
        );
        initializedDate.setHours(
          defaultOfficeHours.startHour,
          defaultOfficeHours.startMinutes,
          0
        );

        // Check if we are already seemingly initialized (to avoid loop)
        // But for now, let's enforce defaults if no URL params found

        // Only update if current is different from target default
        const startDiffers = !currentStart || Math.abs(currentStart.getTime() - initializedDate.getTime()) > 60000;
        
        const initializedEnd = new Date(initializedDate);
        setEndTimeWithDayOverflow(initializedEnd, initializedDate, defaultOfficeHours.endHour, defaultOfficeHours.endMinutes);
        const endDiffers = !currentEnd || Math.abs(currentEnd.getTime() - initializedEnd.getTime()) > 60000;

        if (startDiffers || endDiffers) {
          setCommon((context: ICommonContextStates | null) => ({
            ...context,
            start: initializedDate
          }));
          setStartDate(initializedDate);
          setEndDate(initializedEnd);
        }
      }
    }

    // 4. Reservation Mode
    if (
      reservationModalIsOpen &&
      resourceIdStr &&
      !pendingReservationRef.current
    ) {
      pendingReservationRef.current = { resourceId: resourceIdStr };
    }

    // 5. Non-reservation mode resource selection from URL
    // When navigating to map with a resourceId (e.g., from global search clicking on a person),
    // queue the resource selection to be executed when resources are loaded
    if (
      !reservationModalIsOpen &&
      resourceIdStr &&
      !pendingResourceSelectionRef.current
    ) {
      pendingResourceSelectionRef.current = {
        resourceId: resourceIdStr,
        floorId: floorIdStr || undefined,
        center: true
      };
    }

    // Mark as initialized and store current params
    isInitialized.current = true;
    lastSyncedUrlParams.current = currentParamsString;
  }, [
    router.isReady,
    router.asPath,
    // This effect should only run on initialization or context switch

    // Add stable dependencies
    currentOffice.id,
    currentOffice.time_zone,
    authenticatedUser.offices?.offices,
    floors, // dependency for floor check
    // REMOVED: startDate, endDate, floor?.id (tracked via stateRef)
    controlledOfficeId,
    controlledFloorId,
    controlStartDate,
    initialEndDate,
    reservationModalIsOpen,
    defaultOfficeHoursCalculator,
    timeMode
    // actions like setFloor, changeStartDate are stable refs (hopefully) or we should check them
  ]);

  const [forceMapBuilder, setForceMapBuilder] = React.useState(false);
  const mapData = floor?.map_data ? JSON.parse(floor?.map_data) : undefined;
  const isMapBuilderMap = forceMapBuilder || (!floor?.map_url && !!mapData);

  const poppers = useMapViewOverlaysPopper(false);

  const highlightResource: MapStateActions["highlightResource"] =
    React.useCallback(
      (resId, target) => {
        if (!schedulable || !resId) {
          setHighlightedResource(null);
          return;
        }
        const res = schedulable?.find((x) => x.id === resId);
        if (!res) {
          poppers.setReferenceDetailsHighlightElement(null);
          setHighlightedResource(null);
          return;
        }
        if (isMapBuilderMap) {
          const targetOption = target as MapInteractiveZoneActionOptions;
          poppers.setHighlightResourceCoords({
            x: targetOption.x || 0,
            y: targetOption.y || 0,
            width: targetOption.width || 0,
            height: targetOption.height || 0
          });
        } else {
          poppers.setReferenceDetailsHighlightElement(target as Element);
        }
        setHighlightedResource(res);
      },
      [schedulable, isMapBuilderMap]
    );

  const startReserve = (resource: IResource, preserveData = false) => {
    // Trigger the event instead of calling openReserveModal directly
    // This ensures MapReserveCard's onFocusResource handler is called,
    // which properly preserves form state (including edited times)
    events.trigger("onFocusResource", resource, { noZoom: true });
  };

  const filteredResources = React.useMemo(() => {
    if (!resources) return [];

    if (!resourcesIdFilter || resourcesIdFilter.length === 0) {
      return resources;
    }
    return resources.filter((resource) =>
      resourcesIdFilter.includes(resource.id)
    );
  }, [resources, resourcesIdFilter]);

  const allowEditingPointsAndUploads =
    isTacticStaffAuthenticatedUser ||
    authenticatedUser?.user?.is_being_impersonated;

  const selectResource = React.useCallback(
    (
      resId: string,
      center = false,
      callback?: () => void,
      officeId?: string,
      floorId?: string,
      date?: Date,
      preserveData = false
    ) => {
      if (!schedulable && !resources) {
        return;
      }
      const res =
        schedulable?.find((x) => x.id === resId) ||
        resources?.find((x) => x.id === resId);

      if (officeId && res?.office_id && officeId !== res.office_id) {
        console.warn("Attempted to select resource from different office");
        return;
      }

      if (allowEditingPointsAndUploads) {
        setEditGroupId(resId);
      }

      // Determine target floor - use provided floorId if available, otherwise find it from resources
      const targetFloor = floorId
        ? floorsWithResources?.find((f) => f.id === floorId)
        : floorsWithResources?.find((f) =>
            f.resources.find((x) => x.id === resId)
          );

      const needsFloorSwitch = targetFloor && floor?.id !== targetFloor.id;

      const openOnCurrentFloor = () => {
        //setOpenSidePanel(false);
        highlightResource(null, null);

        if (reservationModalIsOpen && res) {
          startReserve(res, preserveData);
          return;
        }

        if (!center) {
          // Not centering - open modal immediately
          setOpenResourceModal(true);
          setSelectedResource(res || null);
          poppers.setReferenceResDetailsElement(
            document.getElementById(`interactive-${resId}`)
          );
        } else {
          // For centering - trigger zoom; modal will be opened via locate callback
          setLocateResourceId(resId);
        }

        callback?.();
      };

      // In reservation modal context, skip floor switching and zoom logic
      // Just open the reserve modal with the selected resource
      if (reservationModalIsOpen && res) {
        startReserve(res, preserveData);
        return;
      }

      // If we need to switch floors, queue locate for when the new floor SVG is ready
      if (targetFloor && needsFloorSwitch) {
        setFloor(targetFloor);
        setStorageFloor(targetFloor.id);
        pendingLocateRef.current = {
          resourceId: resId,
          center: !!center
        };
        return;
      }

      // Same floor – we can act immediately
      openOnCurrentFloor();
    },
    [
      setFloor,
      floor,
      floorsWithResources,
      resources,
      schedulable,
      allowEditingPointsAndUploads,
      highlightResource,
      poppers,
      setLocateResourceId,
      setOpenResourceModal,
      setSelectedResource,
      pendingLocateRef
    ]
  );

  // Auto-open reserve modal when in reservation mode
  React.useEffect(() => {
    // If we have a specific resourceId, wait for resources to load
    if (pendingReservationRef.current && schedulable) {
      const targetResource = schedulable.find(
        (r) => r.id === pendingReservationRef.current?.resourceId
      );

      if (targetResource) {
        // Use selectResource to ensure we're on the right floor/view
        selectResource(targetResource.id, false);
        pendingReservationRef.current = null;
        hasAutoOpenedRef.current = true;
      }
    }
    // If in reservation mode without resourceId, open reserve modal with first available resource
    else if (
      reservationModalIsOpen &&
      !hasAutoOpenedRef.current &&
      schedulable &&
      schedulable.length > 0 &&
      floor
    ) {
      // Check if we have a resource from duplicate/edit data
      if (reserveData?.resource) {
        selectResource(
          reserveData.resource.id,
          false,
          undefined,
          undefined,
          undefined,
          undefined,
          true
        );
        hasAutoOpenedRef.current = true;
        return;
      }

      hasAutoOpenedRef.current = true;
    }
  }, [
    schedulable,
    // startReserve,
    reservationModalIsOpen,
    // startDate,
    floor,
    currentOffice,
    openReserveModal,
    selectResource,
    reserveData
  ]);

  // Handle pending resource selection from URL (non-reservation mode)
  // This handles the case when navigating to /map from global search with a resourceId
  // We need to wait for resources to load before selecting
  React.useEffect(() => {
    if (!pendingResourceSelectionRef.current || !schedulable) {
      return;
    }

    const pending = pendingResourceSelectionRef.current;
    const targetResource = schedulable.find((r) => r.id === pending.resourceId);

    if (targetResource) {
      selectResource(
        targetResource.id,
        pending.center,
        undefined,
        undefined,
        pending.floorId
      );
      pendingResourceSelectionRef.current = null;
    }
  }, [schedulable, selectResource]);

  const [zonesInfo, setZonesInfo] = React.useState<InteractiveZonesInfo>({
    mapInfo: null,
    zones: []
  });

  // Keep refs to the latest functions
  const selectResourceRef = React.useRef(selectResource);
  const openResourceInfoRef = React.useRef<
    ((resourceId: string) => void) | null
  >(null);

  // Update ref when selectResource changes
  React.useEffect(() => {
    selectResourceRef.current = selectResource;
  }, [selectResource]);

  // Create the openResourceInfo function and keep it in a ref
  React.useEffect(() => {
    openResourceInfoRef.current = (resourceId: string) => {
      // Wait a bit for the DOM to settle after zoom, then open the modal
      setTimeout(() => {
        // Prefer schedulable resource, but fall back to all resources on the floor.
        const resFromSchedulable = schedulable?.find(
          (x) => x.id === resourceId
        );
        const resFromAll = resources?.find((x) => x.id === resourceId);
        const res = resFromSchedulable || resFromAll;

        if (!res) {
          return;
        }

        setSelectedResource(res);
        setOpenResourceModal(true);

        // Wait for the element to be in the DOM and properly positioned
        setTimeout(() => {
          const element = document.getElementById(`interactive-${resourceId}`);
          if (element) {
            poppers.setReferenceResDetailsElement(element);
          }
        }, 300);
      }, 300);
    };
  }, [
    schedulable,
    resources,
    setSelectedResource,
    setOpenResourceModal,
    poppers
  ]);

  // Open full resource details when a locate event completes (e.g. URL-based navigation)
  React.useEffect(() => {
    if (isOnReservationModal) {
      return;
    }
    const handleLocateResource = (resource: IResource | null) => {
      if (!resource) return;

      // For locate events we already have the full resource object,
      // so open the modal directly without depending on schedulable/resources
      // lookup timing.
      setSelectedResource(resource);
      setOpenResourceModal(true);

      // Wait for the element to be in the DOM and properly positioned
      setTimeout(() => {
        const element = document.getElementById(`interactive-${resource.id}`);
        if (element) {
          poppers.setReferenceResDetailsElement(element);
        }
      }, 100);
    };

    events.register("onLocateResource", handleLocateResource);

    return () => {
      events.unregister("onLocateResource", handleLocateResource);
    };
  }, [events, setSelectedResource, setOpenResourceModal, poppers]);

  // Called by floor items when their SVG is ready. If we have a pending locate
  // request targeting this floor, start the locate/zoom flow now.
  const onFloorReady = React.useCallback(
    (floorId: string) => {
      const pending = pendingLocateRef.current;
      if (!pending) return;

      const resFromSchedulable =
        schedulable?.find((x) => x.id === pending.resourceId) || null;
      const resFromAll =
        resources?.find((x) => x.id === pending.resourceId) || null;
      const res = resFromSchedulable || resFromAll;

      if (!res) {
        pendingLocateRef.current = null;
        return;
      }

      // Ensure this resource actually belongs to this floor
      if (res.parent_id && res.parent_id !== floorId) {
        return;
      }

      pendingLocateRef.current = null;

      highlightResource(null, null);

      if (reservationModalIsOpen && res) {
        startReserve(res);
        return;
      }

      if (!pending.center) {
        setOpenResourceModal(true);
        setSelectedResource(res);
        poppers.setReferenceResDetailsElement(
          document.getElementById(`interactive-${res.id}`)
        );
      } else {
        setLocateResourceId(res.id);
      }
    },
    [
      schedulable,
      resources,
      highlightResource,
      setOpenResourceModal,
      setSelectedResource,
      poppers,
      reservationModalIsOpen,
      startReserve
    ]
  );

  // Register a stable wrapper function with command palette on mount only
  React.useEffect(() => {
    const stableSelectResource = (params: MapNavigationParams) => {
      const { resourceId, officeId, floorId, date, endDate, center, callback } = params;

      // Check if we need to change office or date first
      const needsOfficeChange = officeId && officeId !== currentOffice?.id;
      const needsDateChange =
        date &&
        (!_startDate || date.toDateString() !== _startDate.toDateString());

      if (needsOfficeChange || needsDateChange) {
        // Handle office and date changes first
        if (needsOfficeChange && officeId) {
          // Navigate to the office with the date if provided
          const targetOffice = authenticatedUser?.offices?.byId?.[officeId];
          const isToday =
            date && targetOffice
              ? isSameDay(date, convertTZ(new Date(), targetOffice.time_zone))
              : true;
          const effectiveTimeMode = isToday ? "live" : "manual";

          // Change office context
          setUserContext({
            type: "updateCurrentOffice",
            payload: officeId
          });

          // Set the time mode and date
          setTimeMode(effectiveTimeMode);
          if (date) {
            if (effectiveTimeMode === "live") {
              setTimeMode("live");
            } else {
              setStartDate(date);
              const end = getOrCalculateDefaultEndDate(date, endDate);
              setEndDate(end);
            }
          }

          // Wait for office change to propagate, then select resource
          setTimeout(() => {
            selectResourceRef.current(
              resourceId,
              center,
              callback,
              officeId,
              floorId,
              date,
              false
            );
          }, 600);
        } else if (needsDateChange && date) {
          // Only date change, no office change
          const targetOffice = currentOffice;
          const isToday = targetOffice
            ? isSameDay(date, convertTZ(new Date(), targetOffice.time_zone))
            : true;

          if (isToday) {
            setTimeMode("live");
          } else {
            setTimeMode("manual");
            setStartDate(date);
            const end = getOrCalculateDefaultEndDate(date, endDate);
            setEndDate(end);
          }

          // Wait for date change to propagate, then select resource
          setTimeout(() => {
            selectResourceRef.current(
              resourceId,
              center,
              callback,
              officeId,
              floorId,
              date
            );
          }, 300);
        }
      } else {
        // No office or date change needed, just select the resource
        selectResourceRef.current(
          resourceId,
          center,
          callback,
          officeId,
          floorId,
          date
        );
      }
    };

    registerMapNavigator(stableSelectResource);

    // Unregister when component unmounts
    return () => {
      registerMapNavigator(null);
    };
  }, [
    registerMapNavigator,
    currentOffice,
    _startDate,
    authenticatedUser,
    setUserContext,
    setTimeMode,
    setStartDate,
    setEndDate
  ]);

  // Register a stable wrapper for openResourceInfo with command palette on mount only
  React.useEffect(() => {
    const stableOpenResourceInfo = (resourceId: string) => {
      openResourceInfoRef.current?.(resourceId);
    };

    registerOpenResourceInfo(stableOpenResourceInfo);

    return () => {
      registerOpenResourceInfo(null);
    };
  }, [registerOpenResourceInfo]);

  // Register map controllers for office and time changes
  React.useEffect(() => {
    const controllers = {
      changeOffice: (officeId: string) => {
        setUserContext({
          type: "updateCurrentOffice",
          payload: officeId
        });
      },
      changeStartDate: (date: Date) => {
        // Use direct setter if the changeStartDate guard would reject it
        if (endDate === null || startDate === null) {
          setStartDate(date);
          // Set a default end date if none exists
          if (endDate === null) {
            const defaultEnd = getOrCalculateDefaultEndDate(date);
            setEndDate(defaultEnd);
          }
        } else {
          changeStartDate(date);
        }
      },
      changeEndTime: changeEndTime,
      setTimeMode: setTimeMode,
      navigateToOffice: (
        officeId: string,
        date?: Date,
        timeMode?: StartEndTimeMode,
        endDate?: Date
      ) => {
        // Use current startDate as default if date is not provided
        const effectiveDate = date || startDate || new Date();
        const effectiveEndDate = getOrCalculateDefaultEndDate(effectiveDate, endDate);

        // Determine if the date is today - if not, use manual mode instead of live
        const targetOffice = authenticatedUser?.offices?.byId?.[officeId];

        // keep this in case we need it later
        const isToday =
          effectiveDate && targetOffice
            ? isSameDay(
                effectiveDate,
                convertTZ(new Date(), targetOffice.time_zone)
              )
            : true;
            
        const effectiveTimeMode = timeMode || "manual";

        // First, update the URL with new office and parameters
        const mapLink = createMapLink({
          date: format(effectiveDate, "yyyy-MM-dd'T'HH:mm:ss"),
          endTime: effectiveEndDate ? format(effectiveEndDate, "yyyy-MM-dd'T'HH:mm:ss") : undefined,
          officeId: officeId,
          timeMode: effectiveTimeMode
        });
        router.replace(mapLink, undefined, { shallow: true });

        // Change the office - this will trigger a re-render with new office context
        setUserContext({
          type: "updateCurrentOffice",
          payload: officeId
        });

        // After office changes, set the date and time mode
        // Use setTimeout to ensure office context has updated and useEffect has run
        setTimeout(() => {
          setTimeMode(effectiveTimeMode);

          if (effectiveDate && targetOffice) {
            const dateInOfficeTimezone = convertTZ(
              effectiveDate,
              targetOffice.time_zone
            );

            setStartDate(dateInOfficeTimezone);
            const endDateInOfficeTimezone = convertTZ(
              effectiveEndDate,
              targetOffice.time_zone
            );
            setEndDate(endDateInOfficeTimezone);
          }
        }, 100);
      }
    };

    registerMapControllers(controllers);

    return () => {
      registerMapControllers(null);
    };
  }, [
    registerMapControllers,
    setUserContext,
    changeStartDate,
    changeEndTime,
    setTimeMode,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    authenticatedUser
  ]);

  return (
    <MapPageContext.Provider
      value={{
        schedulable,
        floors,
        floorsWithResources,
        usersAtOffice,
        resources: filteredResources,
        allResources: resources,
        floor,
        setFloor,
        startDate,
        endDate,
        currentOffice,
        changeOffice,
        changeEndTime,
        changeStartDate,
        timeMode,
        setTimeMode,
        isDataLoading,
        selectedResource,
        setSelectedResource,
        isMapEditing,
        highlightedResource,
        setHighlightedResource,
        isEmbedded,
        isMapBuilderMap,
        poppers,
        setEditGroupId,
        editGroupId,
        openResourceModal,
        setOpenResourceModal,
        highlightResource,
        mapBuilderData: mapData,
        setForceMapBuilder,
        editingMapDisclosure,
        resourceIdHovering,
        setResourceIdHovering,
        focusedResource,
        setFocusedResource,
        startReserve,
        setResourcesIdFilter,
        setStartDate,
        events,
        amenities,
        amenitiesFilter,
        applyAmenitiesFilter,
        userFocusDisclosure,
        selectResource,
        mapFiltersDisclosure,
        zonesInfo,
        setZonesInfo,
        locateResourceId,
        setLocateResourceId,
        liveStartDate,
        liveEndDate,
        mapChangesCount,
        setMapChangesCount,
        forceMapBuilder,
        filterQuery,
        setFilterQuery,
        onFloorReady,
        isReservationMode: !!reservationModalIsOpen,
        isOnReservationModal,
        resourceInReservation: reserveData?.resource?.id || null,
        fullscreenMapDisclosure,
        defaultTab: section,
        setSection,
        selectedUsersInCalendarIntegrations,
        setSelectedUsersInCalendarIntegrations,
        mapInstanceId,
        isReservationCardIsOpen: isReservationCardIsOpen || (isReserveOpen && !isOnReservationModal)
      }}
    >
      {children}
    </MapPageContext.Provider>
  );
};

export const useMap = () => {
  const context = React.useContext(MapPageContext);
  if (!context) {
    throw new Error("useMap must be used within a MapPageProvider");
  }
  return context;
};
